import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useSectionMetrics } from '../motion/scroll';
import { deviceTier, prefersReducedMotion, webglAvailable } from '../motion/preferences';
import { useLocale } from '../i18n/locale';
import { sectionId } from '../content/site';
import { HOME, STEP_OBJECTS } from './content';
import type { KitStudio } from './kit-studio';

/**
 * How it happens: eleven steps, one at a time, each with a canonical kit object presented as a
 * product shot on a scroll-driven carousel (objects enter from the right and leave to the left). The
 * list (desktop), the sliding list (tablet portrait) or the single active step (phone) follows the
 * scroll; the cyan spine is the same line the threads collapsed into.
 */
const STEPS = STEP_OBJECTS;

export function Process() {
  const { locale } = useLocale(); const PROCESS = HOME[locale].process;
  const ref = useRef<HTMLElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const studio = useRef<KitStudio | null>(null);
  const [active, setActive] = useState(0);
  const [threeD, setThreeD] = useState<'pending' | 'on' | 'off'>('pending');
  const state = useRef({ position: 0.5, visible: false });

  // The studio (three.js) loads only when the section approaches the viewport.
  useEffect(() => {
    const section = ref.current; const element = host.current; if (!section || !element) return;
    let disposed = false;
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting) || studio.current || disposed) return;
      observer.disconnect();
      if (!webglAvailable()) { setThreeD('off'); return; }
      void import('./kit-studio').then(({ KitStudio }) => {
        if (disposed) return;
        try { studio.current = new KitStudio(element, STEPS, { reducedMotion: prefersReducedMotion(), detail: deviceTier() === 'high' ? 'high' : 'standard' }); }
        catch { setThreeD('off'); return; }
        setThreeD('on'); studio.current.setPosition(state.current.position); studio.current.setActive(state.current.visible);
      }).catch(() => setThreeD('off'));
    }, { rootMargin: '60% 0px' });
    observer.observe(section);
    return () => { disposed = true; observer.disconnect(); studio.current?.dispose(); studio.current = null; };
  }, []);

  useSectionMetrics(ref, ({ track, visible }) => {
    state.current.visible = visible; studio.current?.setActive(visible);
    if (!visible) return;
    const position = Math.min(STEPS.length - 0.001, Math.max(0, track * STEPS.length));
    const step = Math.floor(position); state.current = { position, visible };
    setActive(previous => (previous === step ? previous : step));
    ref.current?.style.setProperty('--progress', (position / STEPS.length).toFixed(4));
    studio.current?.setPosition(position);
  });

  // Tablet portrait: the list slides so the active step stays in the middle of its window.
  useEffect(() => {
    const element = list.current; const item = element?.children[active] as HTMLElement | undefined; if (!element || !item) return;
    element.style.setProperty('--shift', `${-(item.offsetTop - element.clientHeight * 0.35)}px`);
  }, [active]);

  const current = PROCESS.steps[active];
  return (
    <section ref={ref} id={sectionId('process', locale)} className="process" data-tone="light" data-3d={threeD} aria-labelledby="process-title" style={{ '--steps': STEPS.length } as CSSProperties}>
      <div className="process__sticky">
        <div className="process__inner wrap">
          <header className="process__head">
            <h2 id="process-title" className="section-title">{PROCESS.title}</h2>
            <p className="section-body">{PROCESS.body}</p>
          </header>
          <div className="process__list-window">
            <ol ref={list} className="process__steps">
              {PROCESS.steps.map((step, index) => (
                <li key={step.title} className={`process__step${index === active ? ' is-active' : index < active ? ' is-done' : ''}`} aria-current={index === active ? 'step' : undefined}>
                  <span className="process__num">{String(index + 1).padStart(2, '0')}</span>
                  <span className="process__step-title">{step.title}</span>
                  <span className="process__step-body">{step.body}</span>
                </li>
              ))}
            </ol>
          </div>
          <figure className="process__studio">
            <div ref={host} className="process__stage" aria-hidden="true" />
            <span className="process__marks" aria-hidden="true"><i /><i /><i /><i /></span>
            <figcaption className="process__meta">
              <span className="process__meta-index"><span className="process__count-now">{String(active + 1).padStart(2, '0')}</span> / {STEPS.length}</span>
              <span className="process__meta-rule" aria-hidden="true" />
              <span className="process__meta-title">{current.title}</span>
            </figcaption>
            <span className="process__ticks" aria-hidden="true">{STEPS.map((_, index) => <span key={index} className={index <= active ? 'is-on' : undefined} />)}</span>
          </figure>
          <p className="process__phone-body" aria-hidden="true">{current.body}</p>
        </div>
      </div>
    </section>
  );
}
